import {
  CLIP_POSTER_OBJECT,
  CLIP_SIDECAR_OBJECT,
  CLIP_VIDEO_OBJECT,
  clipObjectUrl,
  decodePublicHost,
  isValidClipId
} from "@shared/clipLink";

/**
 * Everything a clip page needs, derived purely from the URL.
 *
 * There is no server and no database behind a share link: `?p=` names the
 * bucket and `?c=` names the clip, and those two expand into the object URLs
 * below. A link is therefore only as private as it is unguessable, which is
 * the same guarantee the desktop app makes when it creates one.
 */
export type ClipSource = {
  /** https origin of the user's bucket. */
  origin: string;
  id: string;
  videoUrl: string;
  sidecarUrl: string;
  posterUrl: string;
};

export type ClipSourceError = "missing" | "bad_bucket" | "bad_clip";

export type ClipSourceResult =
  { ok: true; source: ClipSource } | { ok: false; error: ClipSourceError };

/**
 * Validate the query parameters and expand them into object URLs.
 *
 * Both inputs are attacker-controlled — a crafted link is the one thing every
 * visitor to this page has in common — so nothing is trusted through. The
 * codec rebuilds the origin from a validated hostname, and the clip id must
 * look like the ids the app mints before it can be pasted into a path.
 */
export const resolveClipSource = (
  bucketToken: string | undefined,
  clipId: string | undefined
): ClipSourceResult => {
  if (!bucketToken && !clipId) return { ok: false, error: "missing" };

  const origin = decodePublicHost(bucketToken);
  if (!origin) return { ok: false, error: "bad_bucket" };
  if (!isValidClipId(clipId)) return { ok: false, error: "bad_clip" };

  return {
    ok: true,
    source: {
      origin,
      id: clipId,
      videoUrl: clipObjectUrl(origin, clipId, CLIP_VIDEO_OBJECT),
      sidecarUrl: clipObjectUrl(origin, clipId, CLIP_SIDECAR_OBJECT),
      posterUrl: clipObjectUrl(origin, clipId, CLIP_POSTER_OBJECT)
    }
  };
};
